import {
  CheckCircleIcon,
  InformationCircleIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import RightSideSummary from "./RightSideSummary";
import { useNavigate } from "react-router-dom";

// const API_BASE_URL = import.meta?.env?.VITE_API_BASE_URL || ""; 
const API_BASE_URL = 'https://www.gfapi.net' || ""; 

// Example: VITE_API_BASE_URL="https://api.yourdomain.com"

const DEFAULTS = Object.freeze({
  serviceType: "Door to Door",
  insurance: "Included",
  distance: "N/A",
  transitTime: "N/A",
  firstAvailableDate: "N/A",
});

function safeNumber(val) {
  const n = typeof val === "number" ? val : parseFloat(val);
  return Number.isFinite(n) ? n : null;
}

function formatMiles(distanceMiles) {
  const n = safeNumber(distanceMiles);
  if (!n || n <= 0) return DEFAULTS.distance;
  return `${n.toFixed(1)} mi`;
}

function calculateTransitTime(distanceMiles) {
  const d = safeNumber(distanceMiles);
  if (!d || d <= 0) return DEFAULTS.transitTime;

  if (d < 500) return "Same Day or Next Day";
  if (d < 1000) return "2–3 Days";
  if (d < 1500) return "3–5 Days";
  if (d < 2000) return "4–5 Days";
  if (d < 2500) return "4–6 Days";
  return "5–8 Days";
}

function extractCity(location) {
  if (!location) return "";
  if (location.city) return location.city;

  // display example: "20110 Manassas, VA, United States"
  const display = location.display;
  if (!display) return "";

  const firstPart = display.split(",")[0]?.trim() || "";
  const zipCityMatch = firstPart.match(/^\d+\s+(.+)$/);
  return (zipCityMatch ? zipCityMatch[1] : firstPart).trim();
}

function buildStop(location, stopNumber) {
  const city = extractCity(location);
  const state = location?.state || "";
  const postalCode = location?.zip || "";
  const country = location?.country || "";

  const lat = safeNumber(location?.latitude);
  const lng = safeNumber(location?.longitude);

  // Only include lat/lng if valid — prevents wrong hardcoded coords
  const coords =
    lat != null && lng != null
      ? { latitude: lat, longitude: lng }
      : {};

  return {
    stopNumber,
    streetAddress1: "",
    city,
    state,
    postalCode,
    country,
    ...coords,
  };
}

function formatPrice(price) {
  const n = safeNumber(price);
  if (n == null) return "$0";
  return `$${n.toLocaleString()}`;
}

export default function PriceQuote() {
  const { id } = useParams();
const [isSubmitting, setIsSubmitting] = useState(false);
 const navigate = useNavigate();
  const [selectedPrice, setSelectedPrice] = useState("regular");
  const [showDoorTip, setShowDoorTip] = useState(false);
  const [showInsuranceTip, setShowInsuranceTip] = useState(false);

  const [quoteData, setQuoteData] = useState(null);
  const [priceData, setPriceData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [vehicleCondition, setVehicleCondition] = useState("Running");
  const [transportType, setTransportType] = useState("open");
  const [updatingPrice, setUpdatingPrice] = useState(false);

  const fetchQuote = useCallback(async (quoteId) => {
    const res = await fetch(`${API_BASE_URL}/api/quotes/${quoteId}`);
    if (!res.ok) throw new Error(`Quote API failed: ${res.status}`);
    const json = await res.json();
    if (!json?.success) throw new Error("Quote success=false");
    return json.data;
  }, []);

  const fetchPriceData = useCallback(
    async ({ condition, transport, quote }) => {
      if (!quote?.pickup || !quote?.dropoff || !Array.isArray(quote?.vehicles)) {
        throw new Error("Incomplete quote data for pricing");
      }

      const payload = {
        stops: [buildStop(quote.pickup, 1), buildStop(quote.dropoff, 2)],
        vehicles: quote.vehicles.map((v) => ({
          vin: "",
          year: v.year || "",
          make: v.make || "",
          model: v.model || "",
          isOperable: condition === "Running",
          pickupStopNumber: 1,
          dropOffStopNumber: 2,
          vehicleType: (v.vehicleType?.toUpperCase() || "CAR"),
        })),
        isEnclosed: transport === "enclosed",
        limit: 1,
      };

      const res = await fetch(`${API_BASE_URL}/api/market-intelligence/get-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Price API failed: ${res.status}`);

      const json = await res.json();

      // Normalize keys (depends on your API). Keep it defensive:
      return {
        regularPrice: json?.Regular ?? json?.regularPrice ?? null,
        discountPrice: json?.discountPrice ?? json?.Discount ?? null,
        meanPredictedPrice: json?.meanPredictedPrice ?? null,
      };
    },
    []
  );

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        const quote = await fetchQuote(id);
        if (cancelled) return;

        setQuoteData(quote);

        const initialCondition = quote?.vehicles?.[0]?.isOperable ? "Running" : "Non-Running";
        const initialTransport = quote?.carrier === "enclosed" ? "enclosed" : "open";

        setVehicleCondition(initialCondition);
        setTransportType(initialTransport);

        setUpdatingPrice(true);
        const prices = await fetchPriceData({
          condition: initialCondition,
          transport: initialTransport,
          quote,
        });

        if (!cancelled) setPriceData(prices);
      } catch (err) {
        console.error("❌ PriceQuote load error:", err);
        if (!cancelled) {
          // Production-safe fallback: show UI but don't show fake business data
          setQuoteData(null);
          setPriceData(null);
        }
      } finally {
        if (!cancelled) {
          setUpdatingPrice(false);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, fetchQuote, fetchPriceData]);

  const handleVehicleConditionChange = useCallback(
    async (condition) => {
      setVehicleCondition(condition);
      if (!quoteData) return;

      try {
        setUpdatingPrice(true);
        const prices = await fetchPriceData({ condition, transport: transportType, quote: quoteData });
        setPriceData(prices);
      } catch (e) {
        console.error("❌ Update price error:", e);
        setPriceData(null);
      } finally {
        setUpdatingPrice(false);
      }
    },
    [quoteData, transportType, fetchPriceData]
  );

  const handleTransportTypeChange = useCallback(
    async (type) => {
      setTransportType(type);
      if (!quoteData) return;

      try {
        setUpdatingPrice(true);
        const prices = await fetchPriceData({ condition: vehicleCondition, transport: type, quote: quoteData });
        setPriceData(prices);
      } catch (e) {
        console.error("❌ Update price error:", e);
        setPriceData(null);
      } finally {
        setUpdatingPrice(false);
      }
    },
    [quoteData, vehicleCondition, fetchPriceData]
  );

  const bookingSummary = useMemo(() => {
    const pickupCity = extractCity(quoteData?.pickup);
    const dropoffCity = extractCity(quoteData?.dropoff);

    const pickupState = quoteData?.pickup?.state || "";
    const dropoffState = quoteData?.dropoff?.state || "";


    const pickupzip = quoteData?.pickup?.zip || "";
    const dropoffzip = quoteData?.dropoff?.zip || "";

    const distanceMiles = quoteData?.distanceMiles;
    const transitTime = calculateTransitTime(distanceMiles);
   

// helper function: date ko proper format me convert karta hai
const formatDate = (date) => {
  if (!date) return null;

  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// main logic
const pickupDate = quoteData?.pickupDate;
console.log(quoteData,'quoteData')
// agar user ki date full ho / option "date" ho to usi ko pick karo
// warna first available date use karo
const firstAvailableDate =
  pickupDate?.option === 'date'
    ? formatDate(pickupDate.date)
    : quoteData?.pickupDate?.option
  console.log(firstAvailableDate,'firstAvailableDate')
    return {
      distance: formatMiles(distanceMiles),
      
      firstAvailableDate,
      shipFrom: pickupCity || pickupState ? `${pickupCity || ""}${pickupCity && pickupState ? ", " : ""}${pickupState} ${pickupzip}`.trim() : "N/A",
      shipTo: dropoffCity || dropoffState ? `${dropoffCity || ""}${dropoffCity && dropoffState ? ", " : ""}${dropoffState}${dropoffzip}`.trim() : "N/A",
      transportType: transportType === "enclosed" ? "Enclosed" : "Open",
      serviceType: DEFAULTS.serviceType,
      insurance: DEFAULTS.insurance,
      transitTime,
    };
  }, [quoteData, transportType]);

  const vehicleDetails = useMemo(() => {
    const v = quoteData?.vehicles?.[0];

    if (!v) {
      return {
        vehicleMake: "N/A",
        vehicleModel: "N/A",
        vehicleCondition,
        vehicleType: "CAR",
        vehicleSize: "N/A",
      };
    }

    return {
      vehicleMake: `${v.year || ""} ${v.make || ""}`.trim() || "N/A",
      vehicleModel: v.model || "N/A",
      vehicleCondition,
      vehicleType: v.vehicleType || "CAR",
      vehicleSize: v.size || "N/A",
    };
  }, [quoteData, vehicleCondition]);

  const regularPrice = priceData?.regularPrice != null ? formatPrice(priceData.regularPrice) : "N/A";
  const discountPrice = priceData?.discountPrice != null ? formatPrice(priceData.discountPrice) : "N/A";

  const savings = useMemo(() => {
    const reg = safeNumber(priceData?.regularPrice);
    const disc = safeNumber(priceData?.discountPrice);
    if (reg == null || disc == null) return "";
    const s = reg - disc;
    return s > 0 ? `$${s.toFixed(0)}` : "";
  }, [priceData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F7FB] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00A1EF] mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading your quote...</p>
          <p className="text-gray-400 text-sm mt-1">Please wait a moment</p>
        </div>
      </div>
    );
  }

const handleContinueBooking = async () => {
  if (!quoteData?._id || isSubmitting) return;

  setIsSubmitting(true);

  const payload = {
    carrier: transportType,
    distanceMiles: quoteData.distanceMiles,
    vehicles: quoteData.vehicles.map((v) => ({
      ...v,
      isOperable: vehicleCondition === "Running",
    })),
    pricing: {
      regularPrice: priceData?.regularPrice,
      discountPrice: priceData?.discountPrice,
      selectedPriceType: selectedPrice,
      cdPrice:priceData?.meanPredictedPrice
      
    },
    bookingMeta: {
      transitTime: calculateTransitTime(quoteData.distanceMiles),
      serviceType: "Door to Door",
      insurance: "Included",
    },
  };

  try {
    const res = await fetch(
      `${API_BASE_URL}/api/quotes/${quoteData._id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      const errText = await res.text();
      console.error("Update failed:", errText);
      alert("Something went wrong. Please try again.");
      return;
    }

    const json = await res.json();

    if (json?.success !== true) {
      console.error("API error:", json);
      alert("Unable to continue. Please try again.");
      return;
    }

    // ✅ SUCCESS → Navigate
    navigate(`/transport/${quoteData._id}`);

  } catch (err) {
    console.error("Continue booking error:", err);
    alert("Network error. Please check your connection.");
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <div className="p-8 md:p-12 lg:p-16 max-w-7xl mx-auto flex flex-col lg:flex-row gap-12">
        {/* LEFT SIDE */}
        <div className="flex-1">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-[#103558] mb-2">Ta-da!</h1>
            <p className="text-gray-600">Your personalized shipping quote is ready</p>
          </div>

          <div className="bg-gradient-to-r from-[#00A1EF]/10 to-blue-50 border border-[#00A1EF]/20 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <div className="bg-[#00A1EF] p-2 rounded-full mt-0.5">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-lg font-semibold text-[#103558] mb-1">Quote sent to your email</p>
                <p className="text-[#103558] text-sm leading-relaxed">
                  <span className="font-bold text-[#00A1EF]">No payment required</span> to book. Activate your True Price Guarantee.
                </p>
              </div>
            </div>
          </div>

          {/* SERVICE INFO */}
          <div className="flex flex-wrap items-center gap-4 mb-8 bg-white p-5 rounded-xl border border-gray-200">
            <div className="flex items-center gap-2 relative">
              <span className="text-gray-600 text-sm font-medium">Service type</span>
              <span className="font-semibold text-[#00A1EF]">{DEFAULTS.serviceType}</span>
              <InformationCircleIcon
                className="w-4 h-4 text-gray-400 cursor-help"
                onMouseEnter={() => setShowDoorTip(true)}
                onMouseLeave={() => setShowDoorTip(false)}
              />
              {showDoorTip && (
                <div className="absolute top-6 left-0 bg-white border border-gray-200 shadow-md rounded-lg p-3 text-xs text-gray-600 w-56 z-10">
                  Door-to-door pickup & delivery (where accessible for carriers).
                </div>
              )}
            </div>

            <div className="w-px h-6 bg-gray-200" />

            <div className="flex items-center gap-2 relative">
              <span className="text-gray-600 text-sm font-medium">Insurance</span>
              <span className="font-semibold text-[#00A1EF]">{DEFAULTS.insurance}</span>
              <InformationCircleIcon
                className="w-4 h-4 text-gray-400 cursor-help"
                onMouseEnter={() => setShowInsuranceTip(true)}
                onMouseLeave={() => setShowInsuranceTip(false)}
              />
              {showInsuranceTip && (
                <div className="absolute top-6 left-0 bg-white border border-gray-200 shadow-md rounded-lg p-3 text-xs text-gray-600 w-56 z-10">
                  Coverage varies by carrier; details provided at booking.
                </div>
              )}
            </div>
          </div>

          {/* PRICE CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            <div
              onClick={() => setSelectedPrice("regular")}
              className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 bg-white relative overflow-hidden ${
                selectedPrice === "regular" ? "border-[#00A1EF] shadow-md" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              {selectedPrice === "regular" && (
                <div className="absolute top-4 right-4">
                  <div className="bg-[#00A1EF] text-white p-1.5 rounded-full">
                    <CheckCircleIcon className="w-5 h-5" />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#103558]">Regular price</h3>
                {updatingPrice && selectedPrice === "regular" && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                    Updating
                  </div>
                )}
              </div>

              <div className="mb-4">
                <span className="text-4xl font-bold text-[#103558]">{regularPrice}</span>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed">
                Full payment charged to your card once carrier is assigned.
              </p>
            </div>

            <div
              onClick={() => setSelectedPrice("discount")}
              className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 bg-white relative overflow-hidden ${
                selectedPrice === "discount" ? "border-[#00A1EF] shadow-md" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              {selectedPrice === "discount" && (
                <div className="absolute top-4 right-4">
                  <div className="bg-[#00A1EF] text-white p-1.5 rounded-full">
                    <CheckCircleIcon className="w-5 h-5" />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-[#103558]">Discounted cash price</h3>
                  {savings && (
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">
                      Save {savings}
                    </span>
                  )}
                </div>
                {updatingPrice && selectedPrice === "discount" && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                    Updating
                  </div>
                )}
              </div>

              <div className="mb-4">
                <span className="text-4xl font-bold text-[#103558]">{discountPrice}</span>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed">
                Partial card payment, remaining balance due in cash upon delivery.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mb-8">
            
           <button
  onClick={handleContinueBooking}
  disabled={isSubmitting}
  className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300
    ${isSubmitting
      ? "bg-gray-400 cursor-not-allowed"
      : "bg-gradient-to-r from-[#00A1EF] to-[#0088CC] hover:from-[#0088CC] hover:to-[#0077B3] text-white"
    }
  `}
>
  {isSubmitting ? (
    <div className="flex items-center justify-center gap-2">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
      Processing...
    </div>
  ) : (
    "Continue to booking details"
  )}
</button>

            <p className="text-center text-sm text-gray-500 mt-3">Secure your spot in just 2 minutes</p>
          </div>

          {/* SUPPORT OPTIONS (you can env-configure phone/time later) */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
            <div className="relative my-2">
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or book with our team</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <PhoneIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Call Our Experts</h4>
                    <p className="text-xs text-gray-600">Support hours vary</p>
                  </div>
                </div>
                <a
                  href="tel:+18007801113"
                  className="block w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-center transition-colors"
                >
                  Call (800) 780-1113
                </a>
              </div>

              <div className="bg-emerald-50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Live Chat Support</h4>
                    <p className="text-xs text-gray-600">24/7 Instant answers</p>
                  </div>
                </div>
                {/* <button className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg text-center transition-colors">
                  Start Live Chat
                </button> */}
                <button
  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg text-center transition-colors"
  onClick={() => {
    if (window.Tawk_API) {
      window.Tawk_API.toggle(); // Opens the chat window
    }
  }}
>
  Start Live Chat
</button>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <RightSideSummary
          bookingSummary={bookingSummary}
          vehicleDetails={vehicleDetails}
          transportType={transportType}
          onVehicleConditionChange={handleVehicleConditionChange}
          onTransportTypeChange={handleTransportTypeChange}
          editable={false}
          updatingPrice={updatingPrice}
          priceDetails={{
            dueNow: "$0",
            totalDue: selectedPrice === "regular" ? regularPrice : discountPrice,
            priceLabel: "Total due",
          }}
        />
      </div>
    </div>
  );
}
